import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Building2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/integrations/supabase/client';
import BackgroundMediaComponent from '@/components/BackgroundMedia';

// AHP design tokens (shared with AhpGlobalHeader)
const T_LIGHT = {
  bg: '#FAF9F6',
  cardBg: '#ffffff',
  text: '#1B1B1B',
  textMuted: 'rgba(27,27,27,0.62)',
  gold: '#C5A55A',
  red: '#B22234',
  blue: '#002868',
  border: 'rgba(27,27,27,0.12)',
};
const T_DARK = {
  bg: '#0a1628',
  cardBg: '#0d1e35',
  text: '#FAF9F6',
  textMuted: 'rgba(250,249,246,0.55)',
  gold: '#C5A55A',
  red: '#B22234',
  blue: '#5B9BD5',
  border: 'rgba(250,249,246,0.12)',
};

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

type AuthView = 'chooser' | 'magic-link' | 'magic-link-sent' | 'password-signin' | 'password-signup';

// ── Google SVG icon ──
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// ── Apple SVG icon ──
const AppleIcon = ({ color }: { color: string }) => (
  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill={color}>
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

// ══════════════════════════════════════════════════════
// AHP Auth — patriotic two-column layout
// ══════════════════════════════════════════════════════
const AhpAuth = () => {
  const navigate = useNavigate();
  const { signIn, signUp, signInWithMagicLink, user, loading } = useAuth();
  const { toast } = useToast();
  const { isDark } = useTheme();
  const T = isDark ? T_DARK : T_LIGHT;

  const [view, setView] = useState<AuthView>('chooser');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [error, setError] = useState('');

  if (!loading && user) return <Navigate to="/" replace />;

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) { setError(error.message); setIsGoogleLoading(false); }
  };

  const handleAppleSignIn = async () => {
    setIsAppleLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: window.location.origin },
    });
    if (error) { setError(error.message); setIsAppleLoading(false); }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const { error } = await signInWithMagicLink(email);
    if (error) {
      setError(error.message);
    } else {
      setView('magic-link-sent');
    }
    setIsLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const { error } = await signIn(email, password);
    if (error) {
      setError(error.message === 'Invalid login credentials'
        ? 'Invalid email or password. Please check your credentials and try again.'
        : error.message || 'Login failed. Please try again.');
    } else {
      toast({ title: 'Welcome back!', description: 'You have been successfully signed in.' });
      navigate('/');
    }
    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setIsLoading(false);
      return;
    }
    const { error } = await signUp(email, password);
    if (error) {
      setError(error.message === 'User already registered'
        ? 'An account with this email already exists. Please sign in instead.'
        : error.message || 'Account creation failed. Please try again.');
    } else {
      toast({ title: 'Account created!', description: 'Please check your email to confirm your account.' });
    }
    setIsLoading(false);
  };

  const goBack = () => { setView('chooser'); setError(''); };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: T.gold }} />
      </div>
    );
  }

  // ── Shared button styles ──
  const oauthBtnStyle: React.CSSProperties = {
    width: '100%',
    height: 48,
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    color: T.text,
    fontFamily: sans,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  };

  const primaryBtnStyle: React.CSSProperties = {
    width: '100%',
    height: 48,
    borderRadius: 8,
    border: 'none',
    background: T.red,
    color: '#ffffff',
    fontFamily: sans,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 44,
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    color: T.text,
    fontFamily: sans,
    fontSize: 15,
    padding: '0 14px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: sans,
    fontSize: 13,
    fontWeight: 500,
    color: T.text,
    marginBottom: 6,
    display: 'block',
  };

  // ── Render the right-side form based on view state ──
  const renderForm = () => {
    // ── Magic Link Sent confirmation ──
    if (view === 'magic-link-sent') {
      return (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <CheckCircle size={48} style={{ color: '#2b8a3e', margin: '0 auto 16px' }} />
          <h3 style={{ fontFamily: serif, fontSize: 22, color: T.text, margin: '0 0 8px' }}>
            Check your email
          </h3>
          <p style={{ fontFamily: sans, fontSize: 14, color: T.textMuted, margin: '0 0 24px', lineHeight: 1.5 }}>
            We sent a sign-in link to <strong style={{ color: T.text }}>{email}</strong>.
            Click the link in your email to sign in.
          </p>
          <button onClick={goBack} style={{ ...oauthBtnStyle, width: 'auto', padding: '0 24px', margin: '0 auto' }}>
            <ArrowLeft size={16} style={{ marginRight: 8 }} /> Back to sign in
          </button>
        </div>
      );
    }

    // ── Magic Link email entry ──
    if (view === 'magic-link') {
      return (
        <>
          <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: sans, fontSize: 13, color: T.textMuted, padding: 0, marginBottom: 20 }}>
            <ArrowLeft size={14} /> Back
          </button>
          <h3 style={{ fontFamily: serif, fontSize: 22, color: T.text, margin: '0 0 8px' }}>
            Sign in with email
          </h3>
          <p style={{ fontFamily: sans, fontSize: 14, color: T.textMuted, margin: '0 0 24px' }}>
            We&apos;ll send you a magic link to sign in instantly.
          </p>
          <form onSubmit={handleMagicLink}>
            <label style={labelStyle}>Email address</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" style={{ ...inputStyle, marginBottom: 16 }} />
            {error && <div style={{ fontFamily: sans, fontSize: 13, color: T.red, marginBottom: 12 }}>{error}</div>}
            <button type="submit" disabled={isLoading} style={{ ...primaryBtnStyle, opacity: isLoading ? 0.7 : 1 }}>
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 8 }} /> : <Mail size={16} style={{ marginRight: 8 }} />}
              Send magic link
            </button>
          </form>
        </>
      );
    }

    // ── Password Sign In ──
    if (view === 'password-signin') {
      return (
        <>
          <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: sans, fontSize: 13, color: T.textMuted, padding: 0, marginBottom: 20 }}>
            <ArrowLeft size={14} /> Back
          </button>
          <h3 style={{ fontFamily: serif, fontSize: 22, color: T.text, margin: '0 0 24px' }}>
            Sign in with password
          </h3>
          <form onSubmit={handleSignIn}>
            <label style={labelStyle}>Email address</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" style={{ ...inputStyle, marginBottom: 14 }} />
            <label style={labelStyle}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Enter your password" style={{ ...inputStyle, marginBottom: 16 }} />
            {error && <div style={{ fontFamily: sans, fontSize: 13, color: T.red, marginBottom: 12 }}>{error}</div>}
            <button type="submit" disabled={isLoading} style={{ ...primaryBtnStyle, opacity: isLoading ? 0.7 : 1 }}>
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 8 }} />}
              Sign in
            </button>
          </form>
          <p style={{ fontFamily: sans, fontSize: 13, color: T.textMuted, marginTop: 16, textAlign: 'center' }}>
            Don&apos;t have an account?{' '}
            <button onClick={() => { setView('password-signup'); setError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.red, fontWeight: 600, fontFamily: sans, fontSize: 13, padding: 0 }}>
              Create one
            </button>
          </p>
        </>
      );
    }

    // ── Password Sign Up ──
    if (view === 'password-signup') {
      return (
        <>
          <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: sans, fontSize: 13, color: T.textMuted, padding: 0, marginBottom: 20 }}>
            <ArrowLeft size={14} /> Back
          </button>
          <h3 style={{ fontFamily: serif, fontSize: 22, color: T.text, margin: '0 0 24px' }}>
            Create your account
          </h3>
          <form onSubmit={handleSignUp}>
            <label style={labelStyle}>Email address</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" style={{ ...inputStyle, marginBottom: 14 }} />
            <label style={labelStyle}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Create a password (min 6 characters)" minLength={6} style={{ ...inputStyle, marginBottom: 16 }} />
            {error && <div style={{ fontFamily: sans, fontSize: 13, color: T.red, marginBottom: 12 }}>{error}</div>}
            <button type="submit" disabled={isLoading} style={{ ...primaryBtnStyle, opacity: isLoading ? 0.7 : 1 }}>
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 8 }} />}
              Create account
            </button>
          </form>
          <p style={{ fontFamily: sans, fontSize: 13, color: T.textMuted, marginTop: 16, textAlign: 'center' }}>
            Already have an account?{' '}
            <button onClick={() => { setView('password-signin'); setError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.red, fontWeight: 600, fontFamily: sans, fontSize: 13, padding: 0 }}>
              Sign in
            </button>
          </p>
        </>
      );
    }

    // ── Chooser (default) — stacked auth buttons ──
    return (
      <>
        <h3 style={{ fontFamily: serif, fontSize: 24, color: T.text, margin: '0 0 4px' }}>
          Sign in
        </h3>
        <p style={{ fontFamily: sans, fontSize: 14, color: T.textMuted, margin: '0 0 28px' }}>
          Access hospital pricing transparency data
        </p>

        {/* Google */}
        <button onClick={handleGoogleSignIn} disabled={isGoogleLoading || isLoading} style={{ ...oauthBtnStyle, marginBottom: 10 }}>
          {isGoogleLoading ? <Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 12 }} /> : <GoogleIcon />}
          Continue with Google
        </button>

        {/* Apple */}
        <button onClick={handleAppleSignIn} disabled={isAppleLoading || isLoading} style={{ ...oauthBtnStyle, marginBottom: 10 }}>
          {isAppleLoading ? <Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 12 }} /> : <AppleIcon color={T.text} />}
          Continue with Apple
        </button>

        {/* Magic link */}
        <button onClick={() => { setView('magic-link'); setError(''); }} style={{ ...oauthBtnStyle, marginBottom: 10 }}>
          <Mail size={18} style={{ marginRight: 12 }} />
          Email me a sign-in link
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', margin: '18px 0', gap: 12 }}>
          <div style={{ flex: 1, height: 1, background: T.border }} />
          <span style={{ fontFamily: sans, fontSize: 12, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>or</span>
          <div style={{ flex: 1, height: 1, background: T.border }} />
        </div>

        {/* Email/password */}
        <button onClick={() => { setView('password-signin'); setError(''); }} style={{ ...oauthBtnStyle }}>
          Sign in with email &amp; password
        </button>

        {error && <div style={{ fontFamily: sans, fontSize: 13, color: T.red, marginTop: 12, textAlign: 'center' }}>{error}</div>}

        <p style={{ fontFamily: sans, fontSize: 12, color: T.textMuted, marginTop: 24, textAlign: 'center', lineHeight: 1.5 }}>
          By signing in, you agree to our Terms of Service.
        </p>
      </>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: T.bg }}>
      <Helmet>
        <title>Sign In | American Hospital Prices</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'row',
      }}>
        {/* ── Left brand panel (desktop only) ── */}
        <div
          className="hidden md:flex"
          style={{
            width: '45%',
            maxWidth: 520,
            flexShrink: 0,
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px 48px',
            background: isDark
              ? 'linear-gradient(135deg, #0d1e35 0%, #0a1628 100%)'
              : 'linear-gradient(135deg, #002868 0%, #001a44 100%)',
            borderTop: `4px solid ${T.red}`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle decorative stars */}
          <div style={{ position: 'absolute', top: 40, right: 40, fontSize: 120, color: 'rgba(197,165,90,0.06)', lineHeight: 1 }}>&#9733;</div>
          <div style={{ position: 'absolute', bottom: 60, left: 30, fontSize: 80, color: 'rgba(197,165,90,0.04)', lineHeight: 1 }}>&#9733;</div>

          <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 360 }}>
            {/* Gold star */}
            <div style={{ fontSize: 48, color: T.gold, marginBottom: 20, lineHeight: 1 }}>&#9733;</div>

            {/* Brand name */}
            <h1 style={{
              fontFamily: serif,
              fontSize: 36,
              fontWeight: 400,
              color: '#FAF9F6',
              letterSpacing: '-0.5px',
              margin: '0 0 16px',
              lineHeight: 1.15,
            }}>
              American Hospital Prices
            </h1>

            {/* Tagline */}
            <p style={{
              fontFamily: sans,
              fontSize: 16,
              color: 'rgba(250,249,246,0.7)',
              margin: '0 0 32px',
              lineHeight: 1.5,
            }}>
              Know Your Price. Know Your Rights.
            </p>

            {/* Divider */}
            <div style={{ width: 40, height: 2, background: T.gold, margin: '0 auto 32px', opacity: 0.5 }} />

            {/* Value props */}
            <div style={{ fontFamily: sans, fontSize: 14, color: 'rgba(250,249,246,0.55)', lineHeight: 1.8 }}>
              <p style={{ margin: '0 0 6px' }}>Compare real hospital prices near you</p>
              <p style={{ margin: '0 0 6px' }}>Free for every American</p>
              <p style={{ margin: 0 }}>Data from 6,000+ hospitals</p>
            </div>
          </div>
        </div>

        {/* ── Right auth form panel ── */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
        }}>
          <div style={{ width: '100%', maxWidth: 400 }}>
            {/* Mobile brand header (hidden on desktop) */}
            <div className="md:hidden" style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{ fontSize: 32, color: T.gold, marginBottom: 8, lineHeight: 1 }}>&#9733;</div>
              <h1 style={{
                fontFamily: serif,
                fontSize: 24,
                fontWeight: 400,
                color: T.text,
                letterSpacing: '-0.3px',
                margin: '0 0 4px',
              }}>
                American Hospital Prices
              </h1>
              <p style={{ fontFamily: sans, fontSize: 13, color: T.textMuted, margin: 0 }}>
                Know Your Price. Know Your Rights.
              </p>
            </div>

            {/* Auth form card */}
            <div style={{
              background: T.cardBg,
              borderRadius: 12,
              border: `1px solid ${T.border}`,
              padding: '32px 28px',
              boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.3)' : '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              {renderForm()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// Generic Auth — existing card+tabs layout for non-AHP brands
// ══════════════════════════════════════════════════════
const GenericAuth = () => {
  const navigate = useNavigate();
  const { signIn, signUp, signInWithMagicLink, user, loading } = useAuth();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  if (!loading && user) return <Navigate to="/" replace />;

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) { setError(error.message); setIsGoogleLoading(false); }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const { error } = await signIn(email, password);
    if (error) {
      setError(error.message === 'Invalid login credentials'
        ? 'Invalid email or password. Please check your credentials and try again.'
        : error.message || 'Login failed. Please try again.');
    } else {
      toast({ title: 'Welcome back!', description: 'You have been successfully signed in.' });
      navigate('/');
    }
    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setIsLoading(false);
      return;
    }
    const { error } = await signUp(email, password);
    if (error) {
      setError(error.message === 'User already registered'
        ? 'An account with this email already exists. Please sign in instead.'
        : error.message || 'Account creation failed. Please try again.');
    } else {
      toast({ title: 'Account created!', description: 'Please check your email to confirm your account.' });
    }
    setIsLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Sign In</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <BackgroundMediaComponent />

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-background/95 backdrop-blur-sm border border-border shadow-xl">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-primary mr-2" />
              <CardTitle className="text-2xl font-bold">Hospital Price Map</CardTitle>
            </div>
            <CardDescription>
              Sign in to access hospital pricing transparency data
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Button
              variant="outline"
              className="w-full mb-4 h-11"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading || isLoading}
            >
              {isGoogleLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <GoogleIcon />
              )}
              Continue with Google
            </Button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input id="signin-email" type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input id="signin-password" type="password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                  </div>
                  {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</> : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input id="signup-email" type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input id="signup-password" type="password" placeholder="Create a password (min 6 characters)" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
                  </div>
                  {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating account...</> : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>Access hospital pricing data and transparency metrics</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// Router — pick layout based on brand
// ══════════════════════════════════════════════════════
const Auth = () => {
  const brand = useBrand();
  return brand.key === 'ahp' ? <AhpAuth /> : <GenericAuth />;
};

export default Auth;
